import React, { useEffect } from 'react';
import ScrollTop from 'libs/commons/ScrollTop';
import LayoutWrapper from 'LayoutWrapper';
import StateProvider from 'providers/StateProvider';
import { Slide, ToastContainer } from 'react-toastify';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Drawer from 'components/commons/Drawer';
import GaPageTracker from 'components/commons/GaPageTracker';
import NotFoundPage from 'pages/NotFoundPage';
import 'react-toastify/dist/ReactToastify.css';
import CssBaseline from '@mui/material/CssBaseline';
import {
  BottomSheet,
  Confirm,
  Modal,
  ModelStateProvider,
  UniversalModal,
} from 'saladsoft-primitive';
import 'resources/css/Common.css';
import GlobalStyle from 'resources/styles/globalStyle';
import MobileCalendarPortal from 'components/commons/searchBox/MobileCalendarPortal';
import useExitClearSession from 'hooks/useExit';
import ErrorBoundary from 'ErrorBoundary';
import ErrorPage from 'pages/ErrorPage';

const App = () => {
  useExitClearSession();

  return (
    <>
      <CssBaseline />
      <GlobalStyle />
      <BrowserRouter>
        <GlobalErrorHandlers />
        <GaPageTracker />
        {/* <ScrollRestoration /> */}
        <ModelStateProvider>
          <StateProvider>
            <ScrollTop />
            <Drawer />
            <UniversalModal />
            <MobileCalendarPortal />
            <Modal />
            <Confirm />
            <BottomSheet />
            <RouteErrorBoundary>
              <Routes>
                <Route path="/error" element={<ErrorPage />} />
                <Route path="/*" element={<LayoutWrapper />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </RouteErrorBoundary>
            <ToastContainer
              position="bottom-center"
              autoClose={1000}
              style={{ width: '100%', maxWidth: '500px' }}
              hideProgressBar
              newestOnTop
              closeOnClick
              transition={Slide}
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="colored"
            />
          </StateProvider>
        </ModelStateProvider>
      </BrowserRouter>
    </>
  );
};

const RouteErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary key={location.key}>{children}</ErrorBoundary>;
};

export default App;

const GlobalErrorHandlers: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleWindowError = (event: ErrorEvent) => {
      // 리소스 로딩 에러(img/script/css 등)는 제외
      const isResourceError =
        (event as any).target &&
        (event as any).target !== window &&
        !(event.error instanceof Error);
      if (isResourceError) return;

      // 서드파티 스크립트 에러 제외 (facebook, kakao, naver, channel.io 등)
      const thirdPartyPatterns = [
        'connect.facebook.net',
        'sdk.js',
        't1.kakaocdn.net',
        'openapi.map.naver.com',
        'cdn.channel.io',
      ];
      const errorSource = event.filename || event.error?.stack || '';
      const isThirdPartyError = thirdPartyPatterns.some((pattern) =>
        errorSource.includes(pattern),
      );
      if (isThirdPartyError) {
        console.warn('[ThirdParty Error]', event.error);
        return;
      }

      try {
        const details = {
          name: event.error?.name || 'WindowError',
          message: event.message,
          stack: event.error?.stack,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          time: new Date().toISOString(),
        };
        localStorage.setItem('app:lastError', JSON.stringify(details));
      } catch {}
      navigate('/error', { replace: true });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      try {
        const reason = event.reason;
        const details = {
          name: reason?.name || 'UnhandledRejection',
          message: reason?.message || String(reason),
          stack: reason?.stack,
          time: new Date().toISOString(),
        };
        localStorage.setItem('app:lastError', JSON.stringify(details));
      } catch {}
      navigate('/error', { replace: true });
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [navigate]);

  return null;
};
